import { useState } from 'react';
import { get } from '../lib/api';
import { navigate } from '../lib/hooks';
import { useSession } from '../lib/session';
import { useCart, type CartCustomer } from '../lib/cart';
import { formatCents } from '../../shared/money';
import { formatPhone, normalizePhone } from '../../shared/format';
import { ACTIVATION_CARRIERS, ACTIVATION_TYPES, ACTIVATION_TYPES_OFFERED, PHONE_CARRIERS } from '../../shared/orders';
import { TASK_PHOTOS } from '../../shared/photos';
import { Button, Field, MoneyInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { TASK_ART } from '../ui/art';
import { PhotoTile } from '../ui/photo';
import { CustomerSearch } from './CustomerPicker';
import { MegaWirelessPanel } from './MegaWireless';

const cents = (v: string | undefined, d: number) => {
  const n = Math.round(Number(v) * 100);
  return Number.isFinite(n) && n >= 0 ? n : d;
};

// Per-carrier activation portals. A carrier listed here opens its own portal;
// any other carrier falls back to the store's default portal
// (me.settings.carrier_portal_url, i.e. VidaPay).
const CARRIER_PORTALS: Record<string, { url: string; name: string }> = {
  'AT&T': { url: 'https://paymentcenter.qpay123.com/Topup/9/153/27', name: 'QPay' },
  'T-Mobile': { url: 'https://beta.rap.t-mobile.com/rap/home', name: 'T-Mobile RAP' },
};

function Portal({ carrier, label, small = false }: { carrier?: string; label?: string; small?: boolean }) {
  const { me } = useSession();
  const mapped = carrier ? CARRIER_PORTALS[carrier] : undefined;
  const url = mapped?.url ?? me?.settings?.carrier_portal_url;
  const name = mapped?.name ?? 'VidaPay';
  const text = label ?? `Open the carrier portal (${name})`;
  return url ? (
    <a className={small ? 'btn btn-ghost btn-sm' : 'btn btn-secondary'} href={url} target="_blank" rel="noreferrer"><Icon.arrowRight />{text}</a>
  ) : null;
}

function CustomerRow({ customer, onPick }: { customer: CartCustomer | null; onPick: (c: CartCustomer | null) => void }) {
  return (
    <div className="stack-sm">
      <span className="small strong">Customer</span>
      <CustomerSearch value={customer} onChange={onPick} />
    </div>
  );
}

/**
 * Bill pay: the customer pays their carrier bill here. The payment goes to the carrier through
 * Mega Wireless (opened beside STORE OS); only the service fee is the store's. It rings up as its own sale.
 */
export function BillPayScreen() {
  const { me } = useSession();
  const cart = useCart();
  const [carrier, setCarrier] = useState('T-Mobile');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [fee, setFee] = useState<number | null>(cents(me?.settings?.bill_pay_fee, 300));
  const [surcharge, setSurcharge] = useState<number | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [customer, setCustomer] = useState<CartCustomer | null>(cart.cart.customer);
  const digits = account.replace(/\D/g, '');
  const ok = !!carrier && digits.length >= 4 && !!amount && amount > 0;
  const add = () => {
    if (!ok) return;
    if (customer) cart.setCustomer(customer);
    if (!cart.cart.customer && !customer && digits.length >= 10) {
      // Find the customer by the phone being paid, if we know them.
      const p = normalizePhone(digits);
      if (p) get(`/api/member-lookup?code=${p}`).then((r) => r.result && cart.setCustomer({ id: r.result.id, name: r.result.name, phone: r.result.phone, store_credit_cents: r.result.store_credit_cents, points_balance: r.result.points_balance, member_code: r.result.member_code })).catch(() => undefined);
    }
    cart.addBillPay({ carrier, account: digits, amountCents: amount!, feeCents: fee ?? 0, surchargeCents: surcharge ?? 0, confirmation: confirmation.trim() || null });
    navigate('/sale');
  };
  const Art = TASK_ART.billpay;
  return (
    <div className="page">
      <div className="page-head">
        <PhotoTile src={TASK_PHOTOS.billpay} alt="" size={64} icon="bill" fallback={<Art size={56} />} />
        <h1 style={{ marginRight: 'auto' }}>Bill pay</h1>
        <Portal label="Use VidaPay instead" small />
      </div>
      <section className="card stack">
        <h2>Carrier</h2>
        <div className="chips">{PHONE_CARRIERS.map((c) => <button key={c} type="button" className="chip" aria-pressed={carrier === c} onClick={() => setCarrier(c)}>{c}</button>)}</div>
        <div className="form-grid">
          <Field label="Phone or account number"><input className="input input-lg" inputMode="tel" value={account} onChange={(e) => setAccount(e.target.value)} aria-label="Phone or account number" autoFocus /></Field>
          <Field label="Bill amount" hint="what the carrier gets"><MoneyInput value={amount} onChange={setAmount} /></Field>
          <Field label="Service fee" hint="the store keeps this"><MoneyInput value={fee} onChange={setFee} /></Field>
          <Field label="Prepaid surcharge" hint="only if the portal adds it"><MoneyInput value={surcharge} onChange={setSurcharge} /></Field>
          <div className="full"><MegaWirelessPanel account={account} amountCents={amount} onClosed={() => document.getElementById('billpay-confirmation')?.focus()} /></div>
          <Field label="Confirmation number" hint="from Mega Wireless" className="full"><input id="billpay-confirmation" className="input" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} /></Field>
        </div>
        <CustomerRow customer={customer} onPick={setCustomer} />
        <div className="banner"><Icon.info /><div className="small">Do the refill in Mega Wireless first, then type its confirmation number here and add it to the sale. You take the customer’s payment and print their receipt right here in STORE OS. Bill payments aren’t taxed, can’t be discounted, and are reported apart from store sales.</div></div>
        <div className="row-wrap">
          <Button variant="primary" size="lg" icon="bag" disabled={!ok} onClick={add}>Add {formatCents((amount ?? 0) + (fee ?? 0) + (surcharge ?? 0))} to the sale</Button>
        </div>
      </section>
    </div>
  );
}

/** Brand colors for the carrier buttons (the carrier's name on its color, no logos). */
const CARRIER_COLORS: Record<string, string> = { 'T-Mobile': '#E20074', 'AT&T': '#00A8E0', Verizon: '#CD040B', Tracfone: '#0B3D91', 'Simple Mobile': '#F26522' };

/**
 * Activations: T-Mobile, AT&T, Verizon, Tracfone or Simple Mobile; a new line or a port-in. The
 * fee is the store's. What the carrier pays the store is kept in the back office (owner only).
 */
export function ActivationScreen() {
  const { me } = useSession();
  const cart = useCart();
  // Nothing is picked for them: the carrier and the type are always chosen on purpose.
  const [carrier, setCarrier] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [more, setMore] = useState(false);
  const [plan, setPlan] = useState('');
  const [sim, setSim] = useState<'physical' | 'esim'>('physical');
  const [imei, setImei] = useState('');
  const [fee, setFee] = useState<number | null>(cents(me?.settings?.activation_fee, 2_500));
  const [customer, setCustomer] = useState<CartCustomer | null>(cart.cart.customer);
  const missing = !carrier ? 'Pick the carrier' : !type ? 'Pick New Line or Port-In' : null;
  const add = () => {
    if (!carrier || !type) return;
    if (customer) cart.setCustomer(customer);
    cart.addActivation({ carrier, type, phone: phone.replace(/\D/g, '') || null, plan: plan.trim() || null, sim, imei: imei.replace(/\D/g, '') || null, feeCents: fee ?? 0 });
    navigate('/sale');
  };
  const Art = TASK_ART.activation;
  return (
    <div className="page">
      <div className="page-head">
        <PhotoTile src={TASK_PHOTOS.activation} alt="" size={64} icon="sim" fallback={<Art size={56} />} />
        <h1 style={{ marginRight: 'auto' }}>Activation</h1>
        {carrier ? <Portal carrier={carrier} /> : null}
      </div>
      <section className="card stack">
        <h2>1. Carrier</h2>
        <div className="carrier-grid" role="radiogroup" aria-label="Carrier">
          {ACTIVATION_CARRIERS.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={carrier === c} className="carrier-btn" style={{ ['--cc' as any]: CARRIER_COLORS[c] }} onClick={() => setCarrier(c)}>
              <span className="cc-dot" aria-hidden="true" />{c}
            </button>
          ))}
        </div>
        <h2>2. Activation</h2>
        <div className="act-type-grid" role="radiogroup" aria-label="Activation type">
          {ACTIVATION_TYPES_OFFERED.map((k) => (
            <button key={k} type="button" role="radio" aria-checked={type === k} className="act-type" onClick={() => setType(k)}>
              {k === 'new_line' ? <Icon.plus /> : <Icon.swap />}
              <span className="t">{ACTIVATION_TYPES[k]}</span>
              <span className="s">{k === 'new_line' ? 'A brand-new phone number' : 'They keep their number from another carrier'}</span>
            </button>
          ))}
        </div>
        <div className="form-grid">
          <Field label={type === 'port_in' ? 'Number being ported' : 'Phone number'} hint="optional"><input className="input input-lg" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="Phone number" placeholder="(510) 555-0123" /></Field>
          <Field label="Activation fee" hint="not taxed"><MoneyInput value={fee} onChange={setFee} /></Field>
        </div>
        {more ? (
          <div className="form-grid">
            <Field label="Plan" hint="optional"><input className="input" value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="e.g. Unlimited $40" /></Field>
            <Field label="SIM">
              <div className="chips">{(['physical', 'esim'] as const).map((v) => <button key={v} type="button" className="chip" aria-pressed={sim === v} onClick={() => setSim(v)}>{v === 'esim' ? 'eSIM' : 'SIM card'}</button>)}</div>
            </Field>
            <Field label="Phone’s IMEI" hint="optional"><input className="input" inputMode="numeric" value={imei} onChange={(e) => setImei(e.target.value)} /></Field>
          </div>
        ) : <button type="button" className="link-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setMore(true)}>Add plan, SIM or IMEI (optional)</button>}
        <CustomerRow customer={customer} onPick={setCustomer} />
        <div className="row-wrap">
          <Button variant="primary" size="lg" icon="bag" disabled={!!missing} onClick={add}>Add {formatCents(fee ?? 0)} activation to the sale</Button>
          {missing ? <span className="small muted">{missing}</span> : null}
        </div>
      </section>
    </div>
  );
}
